import React from 'react';
import {
  Body,
  Button,
  Checkbox,
  Dropdown,
  Flex,
  Icon,
  Input,
  Menu,
  OptionType,
  Select,
} from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import { Step } from 'components/DrawerSteps/DrawerSteps';
import { LabelListEdit } from 'components/LabelList/LabelListEdit';
import { LabelListDisplay } from 'components/LabelList/LabelListDisplay';
import { ConditionItem } from '../components/Schemas/ViewSchemaPage/ProjectRuleset/components/Conditions/ConditionItem';
import { LogicalRule } from 'utils/rules';
import { Schema } from 'hooks/projects';
import { CascaderSelect } from 'components/CascaderSelect/CascaderSelect';
import { DefaultOptionType } from 'antd/lib/cascader';
import {
  ConditionOperator,
  ConditionOperatorLabel,
  conditionOperatorNeedsValue,
  ConsistencySchemaFilterView,
} from './types';
import {
  useNewConsistencyRuleDrawer,
  Severity,
  Operator,
} from './useNewConsistencyRuleDrawer';
import {
  StyledTextarea,
  SelectWrapper,
  HintText,
} from './DataConsistencyDrawer.styles';

const fallbackDescription = 'No details';

const describeFiltersCount = (count: number): React.ReactNode => {
  if (count === 0) return fallbackDescription;
  return (
    <Body size="medium">
      {count} condition{count === 1 ? '' : 's'} defined
    </Body>
  );
};

const renderFiltersContent = (params: {
  tree: LogicalRule;
  setTree: (value: LogicalRule) => void;
  schemaTypes: Schema[];
  selectedType: Schema | undefined;
  helperText: string;
  nextStep: VoidFunction;
}) => {
  const { tree, setTree, schemaTypes, selectedType, helperText, nextStep } =
    params;

  return (
    <Flex direction="column" gap={16}>
      <Body size="medium">{helperText}</Body>
      <ConditionItem
        condition={tree}
        schemas={schemaTypes}
        root={tree}
        ancestors={[]}
        onRootUpdate={(newTree: LogicalRule) => setTree(newTree)}
        selectedSchema={selectedType}
        enabledPropertyComparison={false}
        showLabels={false}
        isFilter
      />
      <HintText size="small">
        Leave empty to include all instances. You can use AND/OR logic and nest
        conditions in groups for complex filtering.
      </HintText>
      <Button type="primary" onClick={nextStep}>
        Next
      </Button>
    </Flex>
  );
};

const severityOptions: Array<{ label: string; value: Severity }> = [
  { label: 'Critical', value: Severity.Critical },
  { label: 'Warning', value: Severity.Warning },
  { label: 'Minor', value: Severity.Minor },
];

const operatorOptions: Array<{ label: string; value: Operator }> = [
  { label: 'EXISTS IN (values must exist in target)', value: Operator.IN_SET },
  {
    label: 'NOT EXISTS IN (values must not exist in target)',
    value: Operator.NOT_IN_SET,
  },
];

const conditionOperatorOptions: Array<{
  label: string;
  value: ConditionOperator;
}> = Object.values(ConditionOperator).map((op) => ({
  label: ConditionOperatorLabel[op],
  value: op,
}));

export type BuildRuleStepsParams = Omit<
  ReturnType<typeof useNewConsistencyRuleDrawer>,
  | 'currentStep'
  | 'setCurrentStep'
  | 'hasInvalidValues'
  | 'onCreateRule'
  | 'onCancelHandler'
> & {
  schemaFilters: ConsistencySchemaFilterView[];
  isEditing?: boolean;
};

export const buildRuleSteps = (params: BuildRuleStepsParams): Step[] => {
  const {
    creating,
    nameInput,
    setNameInput,
    customerDefinedIdInput,
    setCustomerDefinedIdInput,
    descriptionInput,
    setDescriptionInput,
    severityInput,
    setSeverityInput,
    errorMessageInput,
    setErrorMessageInput,
    labelListInput,
    setLabelListInput,
    sourceSchemaFilterId,
    sourceAttribute,
    setSourceAttribute,
    operator,
    setOperator,
    targetSchemaFilterId,
    targetAttribute,
    setTargetAttribute,
    sourceSchemaFilter,
    targetSchemaFilter,
    sourceAttributes,
    targetAttributes,
    sourceSchemaTypes,
    sourceSelectedType,
    targetSchemaTypes,
    targetSelectedType,
    sourceConditionAttributeOptions,
    targetConditionAttributeOptions,
    sourceFiltersTree,
    setSourceFiltersTree,
    sourceFiltersCount,
    targetFiltersTree,
    setTargetFiltersTree,
    targetFiltersCount,
    hasInvalidName,
    hasInvalidErrorMessage,
    hasInvalidSource,
    hasInvalidTarget,
    nextStep,
    handleSourceSchemaFilterChange,
    handleTargetSchemaFilterChange,
    conditionChecks,
    conditionChecksOnly,
    setConditionChecksOnly,
    addConditionCheck,
    removeConditionCheck,
    updateConditionCheck,
    toggleConditionCheckIsLiteralValue,
    toggleConditionCheckUseTargetSchema,
    schemaFilters,
    isEditing,
  } = params;

  return [
    {
      key: 'general-info',
      title: 'General information',
      subTitle: '',
      description:
        nameInput || descriptionInput ? (
          <>
            {nameInput && (
              <Body size="medium" strong>
                {nameInput}
                {customerDefinedIdInput && ` - ${customerDefinedIdInput}`}
              </Body>
            )}
            {descriptionInput && <Body size="medium">{descriptionInput}</Body>}
            <Body size="medium">Severity: {severityInput}</Body>
          </>
        ) : (
          fallbackDescription
        ),
      content: (
        <Flex direction="column" gap={16}>
          <Body size="medium">
            The general information will be displayed in the rule overview.
          </Body>
          <Input
            fullWidth
            placeholder="Name"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
          />
          <Input
            fullWidth
            placeholder="Rule number (optional)"
            value={customerDefinedIdInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCustomerDefinedIdInput(e.target.value)
            }
          />
          <StyledTextarea
            placeholder="Description"
            value={descriptionInput}
            onChange={(e) => setDescriptionInput(e.target.value)}
            rows={3}
          />
          <SelectWrapper>
            <Body size="small" strong>
              Severity
            </Body>
            <Select
              fullWidth
              closeMenuOnSelect
              value={
                severityInput
                  ? severityOptions.find((s) => s.value === severityInput)
                  : undefined
              }
              placeholder="Select severity"
              onChange={(option: OptionType<Severity>) =>
                setSeverityInput(option?.value ?? Severity.Critical)
              }
              options={severityOptions}
            />
          </SelectWrapper>
          <Button type="primary" disabled={hasInvalidName} onClick={nextStep}>
            Next
          </Button>
        </Flex>
      ),
      invalid: hasInvalidName,
    },
    {
      key: 'error-message',
      title: 'Error message',
      subTitle: '',
      description: errorMessageInput ? (
        <Body size="medium">{errorMessageInput}</Body>
      ) : (
        fallbackDescription
      ),
      content: (
        <Flex direction="column" gap={16}>
          <Body size="medium">
            Define the error message shown when this rule fails validation.
          </Body>
          <StyledTextarea
            placeholder="e.g., Asset must be referenced in WorkOrder"
            value={errorMessageInput}
            onChange={(e) => setErrorMessageInput(e.target.value)}
            rows={3}
          />
          <Button
            type="primary"
            disabled={hasInvalidErrorMessage}
            onClick={nextStep}
          >
            Next
          </Button>
        </Flex>
      ),
      invalid: hasInvalidErrorMessage,
    },
    {
      key: 'labels',
      title: 'Labels',
      subTitle: '',
      description: labelListInput.length ? (
        <LabelListDisplay
          labels={labelListInput.map((label) => ({ text: label, key: label }))}
          limitLabels={7}
        />
      ) : (
        fallbackDescription
      ),
      content: (
        <Flex direction="column" gap={16}>
          <Body size="medium">
            The labels can be used as filters in the tool.
          </Body>
          <LabelListEdit
            disabled={creating}
            labels={labelListInput}
            onChange={(labels: string[]) => setLabelListInput(labels)}
          />
          <Button type="primary" onClick={nextStep}>
            Next
          </Button>
        </Flex>
      ),
      invalid: false,
    },
    {
      key: 'source',
      title: 'Source',
      subTitle: '',
      description: sourceSchemaFilterId ? (
        <>
          <Body size="medium" strong>
            {sourceSchemaFilter?.name} ({sourceSchemaFilter?.schemaType})
          </Body>
          {sourceAttribute && (
            <Body size="medium">Attribute: {sourceAttribute}</Body>
          )}
        </>
      ) : (
        fallbackDescription
      ),
      content: (
        <Flex direction="column" gap={16}>
          <Body size="medium">
            Select the source schema filter and attribute to check. The values
            from this attribute will be validated against the target.
          </Body>
          <SelectWrapper>
            <Body size="small" strong>
              Source Schema Filter
            </Body>
            <Select
              fullWidth
              closeMenuOnSelect
              value={
                sourceSchemaFilterId
                  ? {
                      label: sourceSchemaFilter?.name || '',
                      value: sourceSchemaFilterId,
                    }
                  : undefined
              }
              placeholder="Select source schema filter"
              onChange={(option: OptionType<string>) =>
                handleSourceSchemaFilterChange(option?.value ?? '')
              }
              options={schemaFilters.map((r) => ({
                label: `${r.schemaCollectionName} > ${r.name} (${r.schemaType})`,
                value: r.externalId,
              }))}
            />
          </SelectWrapper>
          <SelectWrapper>
            <Body size="small" strong>
              Source Attribute
            </Body>
            <Select
              fullWidth
              closeMenuOnSelect
              value={
                sourceAttribute
                  ? { label: sourceAttribute, value: sourceAttribute }
                  : undefined
              }
              placeholder="Select attribute"
              onChange={(option: OptionType<string>) =>
                setSourceAttribute(option?.value ?? '')
              }
              options={sourceAttributes.map((attr) => ({
                label: attr,
                value: attr,
              }))}
              isDisabled={!sourceSchemaFilterId}
            />
          </SelectWrapper>
          <Button type="primary" disabled={hasInvalidSource} onClick={nextStep}>
            Next
          </Button>
        </Flex>
      ),
      invalid: hasInvalidSource,
    },
    {
      key: 'source-filters',
      title: 'Source Filters (Optional)',
      subTitle: '',
      description: describeFiltersCount(sourceFiltersCount),
      content: !sourceSchemaFilterId ? (
        <Body size="medium">
          Please select a source schema filter in the previous step before
          defining source filters.
        </Body>
      ) : (
        renderFiltersContent({
          tree: sourceFiltersTree,
          setTree: setSourceFiltersTree,
          schemaTypes: sourceSchemaTypes,
          selectedType: sourceSelectedType,
          helperText:
            'Optionally narrow down the source dataset for this rule. These conditions are applied in addition to the source Schema Filter.',
          nextStep,
        })
      ),
      invalid: false,
    },
    {
      key: 'target',
      title: 'Target & Operator',
      subTitle: '',
      description: targetSchemaFilterId ? (
        <>
          <Body size="medium">
            Operator:{' '}
            {operator === Operator.IN_SET ? 'EXISTS IN' : 'NOT EXISTS IN'}
          </Body>
          <Body size="medium" strong>
            {targetSchemaFilter?.name} ({targetSchemaFilter?.schemaType})
          </Body>
          {targetAttribute && (
            <Body size="medium">Attribute: {targetAttribute}</Body>
          )}
        </>
      ) : (
        fallbackDescription
      ),
      content: (
        <Flex direction="column" gap={16}>
          <Body size="medium">
            Select the operator and target schema filter. The source values will
            be checked against the target attribute.
          </Body>
          <SelectWrapper>
            <Body size="small" strong>
              Operator
            </Body>
            <Select
              fullWidth
              closeMenuOnSelect
              value={
                operator
                  ? operatorOptions.find((o) => o.value === operator)
                  : undefined
              }
              placeholder="Select operator"
              onChange={(option: OptionType<Operator>) =>
                setOperator(option?.value ?? Operator.IN_SET)
              }
              options={operatorOptions}
            />
          </SelectWrapper>
          <SelectWrapper>
            <Body size="small" strong>
              Target Schema Filter
            </Body>
            <Select
              fullWidth
              closeMenuOnSelect
              value={
                targetSchemaFilterId
                  ? {
                      label: targetSchemaFilter?.name || '',
                      value: targetSchemaFilterId,
                    }
                  : undefined
              }
              placeholder="Select target schema filter"
              onChange={(option: OptionType<string>) =>
                handleTargetSchemaFilterChange(option?.value ?? '')
              }
              options={schemaFilters
                .filter((r) => r.externalId !== sourceSchemaFilterId)
                .map((r) => ({
                  label: `${r.schemaCollectionName} > ${r.name} (${r.schemaType})`,
                  value: r.externalId,
                }))}
            />
          </SelectWrapper>
          <SelectWrapper>
            <Body size="small" strong>
              Target Attribute
            </Body>
            <Select
              fullWidth
              closeMenuOnSelect
              value={
                targetAttribute
                  ? { label: targetAttribute, value: targetAttribute }
                  : undefined
              }
              placeholder="Select attribute"
              onChange={(option: OptionType<string>) =>
                setTargetAttribute(option?.value ?? '')
              }
              options={targetAttributes.map((attr) => ({
                label: attr,
                value: attr,
              }))}
              isDisabled={!targetSchemaFilterId}
            />
          </SelectWrapper>
          <Button type="primary" disabled={hasInvalidTarget} onClick={nextStep}>
            Next
          </Button>
        </Flex>
      ),
      invalid: hasInvalidTarget,
    },
    {
      key: 'target-filters',
      title: 'Target Filters (Optional)',
      subTitle: '',
      description: describeFiltersCount(targetFiltersCount),
      content: !targetSchemaFilterId ? (
        <Body size="medium">
          Please select a target schema filter in the previous step before
          defining target filters.
        </Body>
      ) : (
        renderFiltersContent({
          tree: targetFiltersTree,
          setTree: setTargetFiltersTree,
          schemaTypes: targetSchemaTypes,
          selectedType: targetSelectedType,
          helperText:
            'Optionally narrow down the target dataset for this rule. These conditions are applied in addition to the target Schema Filter.',
          nextStep,
        })
      ),
      invalid: false,
    },
    {
      key: 'condition-checks',
      title: 'Condition Checks (Optional)',
      subTitle: '',
      description: (() => {
        const validChecks = conditionChecks.filter(
          (c) =>
            c.sourceAttribute &&
            (!conditionOperatorNeedsValue(c.operator) || c.targetAttribute)
        );
        if (validChecks.length === 0) return fallbackDescription;
        return (
          <>
            {validChecks.map((check) => {
              const sf =
                check.isLiteralValue && check.useTargetSchema
                  ? targetSchemaFilter
                  : sourceSchemaFilter;
              return (
                <Body size="medium" key={check.id}>
                  {check.sourceAttribute && sf ? `[${sf.name}].` : ''}
                  {check.sourceAttribute}{' '}
                  {ConditionOperatorLabel[check.operator]}
                  {conditionOperatorNeedsValue(check.operator) &&
                    check.targetAttribute &&
                    (check.isLiteralValue
                      ? ` "${check.targetAttribute}"`
                      : ` ${
                          targetSchemaFilter
                            ? `[${targetSchemaFilter.name}].`
                            : ''
                        }${check.targetAttribute}`)}
                </Body>
              );
            })}
          </>
        );
      })(),
      content:
        !sourceSchemaFilterId || !targetSchemaFilterId ? (
          <Body size="medium">
            Please select source and target schema filters in the previous steps
            before configuring condition checks.
          </Body>
        ) : (
          <Flex direction="column" gap={8}>
            <Body size="medium">
              Optionally add condition checks that run on matched rows. These
              verify that additional attributes meet specific conditions between
              source and target rows that already passed the primary existence
              check.
            </Body>
            {conditionChecks.length > 0 && (
              <ConditionChecksOnlyCallout>
                <Checkbox
                  checked={conditionChecksOnly}
                  onChange={(_, next) => setConditionChecksOnly(Boolean(next))}
                >
                  <CalloutLabel>
                    <Body size="medium" strong>
                      Include only condition check failures in the report
                    </Body>
                    <Body size="small">
                      Drops rows that fail the existence check. Only
                      condition-check failures are reported and counted.
                    </Body>
                  </CalloutLabel>
                </Checkbox>
              </ConditionChecksOnlyCallout>
            )}
            {conditionChecks.map((check) => (
              <ConditionCheckCard key={check.id}>
                <ConditionCheckHeader>
                  <Body size="x-small">
                    {check.isLiteralValue
                      ? 'Compare to literal value'
                      : 'Compare to property'}
                  </Body>
                  <Dropdown
                    content={
                      <Menu>
                        <Menu.Item
                          onClick={() =>
                            toggleConditionCheckIsLiteralValue(check.id)
                          }
                          icon="GraphTree"
                          iconPlacement="left"
                          aria-label="Toggle property comparison"
                        >
                          <Flex
                            justifyContent="space-between"
                            alignItems="center"
                            gap={8}
                          >
                            <span>Property comparison</span>
                            {!check.isLiteralValue && <Icon type="Checkmark" />}
                          </Flex>
                        </Menu.Item>
                        {check.isLiteralValue && (
                          <Menu.Item
                            onClick={() =>
                              toggleConditionCheckUseTargetSchema(check.id)
                            }
                            icon="DataSource"
                            iconPlacement="left"
                            aria-label="Toggle target schema attribute"
                          >
                            <Flex
                              justifyContent="space-between"
                              alignItems="center"
                              gap={8}
                            >
                              <span>Use target schema attribute</span>
                              {check.useTargetSchema && (
                                <Icon type="Checkmark" />
                              )}
                            </Flex>
                          </Menu.Item>
                        )}
                        <Menu.Item
                          onClick={() => removeConditionCheck(check.id)}
                          icon="Delete"
                          iconPlacement="left"
                          aria-label="Remove condition check"
                          destructive
                        >
                          <span>Remove condition</span>
                        </Menu.Item>
                      </Menu>
                    }
                  >
                    <Button
                      icon="EllipsisVertical"
                      type="ghost"
                      size="small"
                      aria-label="Condition check options"
                    />
                  </Dropdown>
                </ConditionCheckHeader>
                <ConditionCheckLine>
                  <SchemaLabel size="x-small" strong>
                    {check.isLiteralValue && check.useTargetSchema
                      ? targetSchemaFilter?.name
                      : sourceSchemaFilter?.name}
                  </SchemaLabel>
                  <ConditionSelectWrapper>
                    <CascaderSelect
                      multiple={false}
                      options={
                        check.isLiteralValue && check.useTargetSchema
                          ? targetConditionAttributeOptions
                          : sourceConditionAttributeOptions
                      }
                      allowClear={false}
                      value={
                        check.sourceAttribute
                          ? check.sourceAttribute.split('.')
                          : []
                      }
                      placeholder="Attribute"
                      changeOnSelect
                      expandTrigger="hover"
                      displayRender={(label: string[]) => label.join('.')}
                      getPopupContainer={(node: HTMLElement) => node}
                      onChange={(
                        _: unknown,
                        selectedOptions: DefaultOptionType[]
                      ) => {
                        const attributePath = (selectedOptions || [])
                          .map((opt: DefaultOptionType) => opt.value)
                          .filter((v) => v != null)
                          .join('.');
                        updateConditionCheck(
                          check.id,
                          'sourceAttribute',
                          attributePath
                        );
                      }}
                      disabled={!sourceSchemaFilterId}
                    />
                  </ConditionSelectWrapper>
                </ConditionCheckLine>
                <ConditionCheckLine>
                  <SchemaLabelSpacer />
                  <ConditionSelectWrapper>
                    <Select
                      fullWidth
                      closeMenuOnSelect
                      value={conditionOperatorOptions.find(
                        (o) => o.value === check.operator
                      )}
                      onChange={(option: OptionType<ConditionOperator>) =>
                        updateConditionCheck(
                          check.id,
                          'operator',
                          option?.value ?? ConditionOperator.EQUALS
                        )
                      }
                      options={conditionOperatorOptions}
                    />
                  </ConditionSelectWrapper>
                </ConditionCheckLine>
                {conditionOperatorNeedsValue(check.operator) && (
                  <ConditionCheckLine>
                    {check.isLiteralValue ? (
                      <>
                        <SchemaLabelSpacer />
                        <ConditionSelectWrapper>
                          <Input
                            fullWidth
                            placeholder="Literal value"
                            value={check.targetAttribute}
                            onChange={(e) =>
                              updateConditionCheck(
                                check.id,
                                'targetAttribute',
                                e.target.value
                              )
                            }
                          />
                        </ConditionSelectWrapper>
                      </>
                    ) : (
                      <>
                        <SchemaLabel size="x-small" strong>
                          {targetSchemaFilter?.name}
                        </SchemaLabel>
                        <ConditionSelectWrapper>
                          <CascaderSelect
                            multiple={false}
                            options={targetConditionAttributeOptions}
                            allowClear={false}
                            value={
                              check.targetAttribute
                                ? check.targetAttribute.split('.')
                                : []
                            }
                            placeholder="Target attribute"
                            changeOnSelect
                            expandTrigger="hover"
                            displayRender={(label: string[]) => label.join('.')}
                            getPopupContainer={(node: HTMLElement) => node}
                            onChange={(
                              _: unknown,
                              selectedOptions: DefaultOptionType[]
                            ) => {
                              const attributePath = (selectedOptions || [])
                                .map((opt: DefaultOptionType) => opt.value)
                                .filter((v) => v != null)
                                .join('.');
                              updateConditionCheck(
                                check.id,
                                'targetAttribute',
                                attributePath
                              );
                            }}
                            disabled={!targetSchemaFilterId}
                          />
                        </ConditionSelectWrapper>
                      </>
                    )}
                  </ConditionCheckLine>
                )}
              </ConditionCheckCard>
            ))}
            <Button type="ghost" icon="Plus" onClick={addConditionCheck}>
              Add condition check
            </Button>
          </Flex>
        ),
      invalid: false,
    },
  ];
};

const ConditionChecksOnlyCallout = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 6px;
  background: var(--cogs-surface--status-warning--muted--default);
  border-left: 4px solid var(--cogs-border--status-warning--strong);
`;

const CalloutLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ConditionCheckCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 0.75rem;
  border: 1px solid var(--cogs-border-default);
  border-radius: 4px;
`;

const ConditionCheckHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ConditionCheckLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SCHEMA_LABEL_WIDTH = '100px';

const SchemaLabel = styled(Body)`
  color: var(--cogs-greyscale-grey7);
  white-space: nowrap;
  width: ${SCHEMA_LABEL_WIDTH};
  flex-shrink: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SchemaLabelSpacer = styled.div`
  width: ${SCHEMA_LABEL_WIDTH};
  flex-shrink: 0;
`;

const ConditionSelectWrapper = styled(SelectWrapper)`
  margin-top: 0;
`;
